import { createContext, PropsWithChildren, useCallback, useContext, useMemo, useRef } from 'react';

interface FilterableItemRegistryContextValue {
	registerItem: (index: number, data: unknown) => void;
	unregisterItem: (index: number) => void;
}

/** Provides the render index of the current slot item to FilterableItem. */
export const FilterableItemIndexContext = createContext<number>(0);

const FilterableItemRegistryContext = createContext<FilterableItemRegistryContextValue | undefined>(
	undefined,
);

/**
 * Placed above the UniformSlot in FilterableGallery. Collects raw data objects
 * registered by each FilterableItem child, keyed by self-assigned render index.
 */
export const FilterableItemRegistryProvider = ({
	children,
	onRegistryChange,
}: PropsWithChildren<{ onRegistryChange: (data: unknown[]) => void }>) => {
	const registryRef = useRef<Map<number, unknown>>(new Map());

	const prevDataRef = useRef<unknown[]>([]);

	const notify = useCallback(() => {
		const sorted = [...registryRef.current.entries()]
			.sort(([a], [b]) => a - b)
			.map(([, data]) => data);
		// Only notify if the data actually changed to prevent infinite re-render loops.
		const prev = prevDataRef.current;
		if (sorted.length === prev.length && sorted.every((d, i) => d === prev[i])) {
			return;
		}
		prevDataRef.current = sorted;
		onRegistryChange(sorted);
	}, [onRegistryChange]);

	const registerItem = useCallback(
		(index: number, data: unknown) => {
			registryRef.current.set(index, data);
			notify();
		},
		[notify],
	);

	const unregisterItem = useCallback((index: number) => {
		registryRef.current.delete(index);
		// Don't notify synchronously on unregister — this runs during React's
		// commit/cleanup phase and calling setState here causes infinite loops.
		// The subsequent registerItem call (when the component remounts or data
		// changes) will notify with the correct up-to-date array.
	}, []);

	const contextValue = useMemo(
		() => ({ registerItem, unregisterItem }),
		[registerItem, unregisterItem],
	);

	return (
		<FilterableItemRegistryContext.Provider value={contextValue}>
			{children}
		</FilterableItemRegistryContext.Provider>
	);
};

/** Used by FilterableItem to register its data with the parent gallery. */
export const useFilterableItemRegistry = () => useContext(FilterableItemRegistryContext);
